import axios, { AxiosError } from "axios"

// ── Types ──────────────────────────────────────────────────────────────

export interface PaymentsClient {
  id: string
  first_name: string
  last_name: string
  email: string
  company_name: string
  address_line1?: string
  city?: string
  state?: string
  zip?: string
  country?: string
}

export interface PaymentsBankAccount {
  id: string
  client_id: string
  account_holder_name: string
  bank_name: string
  transfer_type: "ACH" | "WIRE"
  account_label?: string
  account_type?: string
  bank_country?: string
  swift_bic_code?: string
  plaid_account_id?: string
  is_active?: boolean
  is_verified?: boolean
  created_at?: string
}

export interface CreatePaymentsClientPayload {
  first_name: string
  last_name: string
  email: string
  company_name: string
  address_line1?: string
  city?: string
  state?: string
  zip?: string
  country?: string
}

export interface PlaidPaymentPayload {
  client_id: string
  public_token: string
  account_id: string
  amount: number
  currency?: string
  effective_date?: string
  description?: string
  notes?: string
}

export interface ManualPaymentPayload {
  client_id: string
  transfer_type: "ACH" | "WIRE"
  account_holder_name: string
  bank_name: string
  amount: number
  bank_country?: string
  account_number?: string
  routing_number?: string
  iban?: string
  swift_bic_code?: string
  currency?: string
  effective_date?: string
  description?: string
  notes?: string
  account_type?: string
  account_label?: string
  bank_address?: string
}

export interface PaymentsTransaction {
  id: string
  transaction_number?: string
  status: string
  amount: number
  currency: string
  transfer_type: "ACH" | "WIRE"
  client_id: string
  bank_account_id: string
  approved_by?: string
  effective_date?: string
  description?: string
}

export interface PaymentResult {
  transaction: PaymentsTransaction
  bank_account_id: string
}

export interface PlaidBalanceAccount {
  plaid_account_id: string
  name: string
  available: number | null
  current: number | null
  currency: string
}

export class PaymentsApiError extends Error {
  status: number
  details?: unknown

  constructor(status: number, message: string, details?: unknown) {
    super(message)
    this.name = "PaymentsApiError"
    this.status = status
    this.details = details
  }
}

type ApiEnvelope<T> = {
  data: T
  message?: string
}

// ── Axios instance ─────────────────────────────────────────────────────
// All payment requests go through the portal backend proxy at /payments/*.
// The backend handles service auth with the payments server — no secrets
// are exposed to the browser.

const portalBaseUrl = process.env.REACT_APP_SERVER_URL?.trim() || "http://localhost:8080"

const paymentsAxios = axios.create({
  baseURL: portalBaseUrl + "/payments",
  headers: { "Content-Type": "application/json" },
  withCredentials: true, // send portal session cookie
})

// Handle errors with structured PaymentsApiError
paymentsAxios.interceptors.response.use(
  (response) => response,
  (error: AxiosError<{ error?: string; message?: string; details?: unknown }>) => {
    const status = error.response?.status ?? 0
    const body = error.response?.data

    if (status === 401) {
      return Promise.reject(new PaymentsApiError(401, "Session expired. Please log in again."))
    }
    if (status === 403) {
      return Promise.reject(new PaymentsApiError(403, "You are not authorized to perform this action."))
    }
    if (status === 400) {
      const msg = body?.error || body?.message || "Invalid request. Please check your input."
      return Promise.reject(new PaymentsApiError(400, msg, body?.details))
    }
    if (status === 409) {
      const msg = body?.error || body?.message || "Conflict."
      return Promise.reject(new PaymentsApiError(409, msg, body?.details))
    }
    if (status >= 500) {
      return Promise.reject(new PaymentsApiError(status, "Server error. Please try again later."))
    }
    return Promise.reject(
      new PaymentsApiError(status || 0, error.message || "Network error. Please check your connection.")
    )
  }
)

// ── Plaid ───────────────────────────────────────────────────────────────

export const createPlaidLinkToken = async (payload: { client_id: string }) => {
  const response = await paymentsAxios.post<ApiEnvelope<{ link_token: string; expiration: string }>>(
    "/plaid/link-token",
    payload,
  )
  return response.data.data
}

export const submitPlaidPayment = async (payload: PlaidPaymentPayload) => {
  const response = await paymentsAxios.post<ApiEnvelope<PaymentResult>>(
    "/plaid/payment",
    payload,
  )
  return response.data.data
}

export const getPlaidBalance = async (accountId: string) => {
  const response = await paymentsAxios.get<ApiEnvelope<PlaidBalanceAccount[]>>(
    `/plaid/balance/${accountId}`,
  )
  return response.data.data
}

// ── Manual payments ─────────────────────────────────────────────────────

export const submitManualPayment = async (payload: ManualPaymentPayload) => {
  const response = await paymentsAxios.post<ApiEnvelope<PaymentResult>>(
    "/manual",
    payload,
  )
  return response.data.data
}

// ── Client management ──────────────────────────────────────────────────

export const getPaymentsClientByEmail = async (email: string): Promise<PaymentsClient | null> => {
  try {
    const response = await paymentsAxios.get<ApiEnvelope<PaymentsClient>>("/clients", {
      params: { email },
    })
    return response.data.data
  } catch (error) {
    if (error instanceof PaymentsApiError && error.status === 404) {
      return null
    }
    throw error
  }
}

export const createPaymentsClient = async (payload: CreatePaymentsClientPayload) => {
  const response = await paymentsAxios.post<ApiEnvelope<PaymentsClient>>("/clients", payload)
  return response.data.data
}

export const updatePaymentsClient = async (clientId: string, payload: Partial<CreatePaymentsClientPayload>) => {
  const response = await paymentsAxios.put<ApiEnvelope<PaymentsClient>>(`/clients/${clientId}`, payload)
  return response.data.data
}

export const listPaymentsBankAccounts = async (clientId: string) => {
  const response = await paymentsAxios.get<ApiEnvelope<PaymentsBankAccount[]>>("/bank-accounts", {
    params: { client_id: clientId },
  })
  return response.data.data
}

// ── Cash flows & payment history ─────────────────────────────────────

export interface CashFlow {
  id: number
  deal_id: number
  date: string
  amount: number
  memo: string | null
}

export interface PaymentRecord {
  id: string
  client_id: string
  amount: number
  status: string
  type: string
  memo: string | null
  created_at: string
  completed_at: string | null
}

export const getPaymentsByClientId = async (clientId: string): Promise<PaymentRecord[]> => {
  try {
    const response = await paymentsAxios.get<ApiEnvelope<PaymentRecord[]>>("/list", {
      params: { client_id: clientId },
    })
    return response.data.data
  } catch {
    return []
  }
}

export const getCashFlowsByDealId = async (dealId: string): Promise<CashFlow[]> => {
  try {
    const response = await paymentsAxios.get<ApiEnvelope<CashFlow[]>>(`/cash-flows/${dealId}`)
    return response.data.data
  } catch {
    return []
  }
}
